import Button from "@components/Button"
import Layout from "@components/Layout"
import Mascot, { MascotState } from "@components/Mascot"
import NumberInputWithUnit from "@components/NumberInputWithUnit"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form } from "react-router-dom"

interface UnitConfig {
  value: string
  label: string
  placeholder: string
  conversionFactor: number
}

interface Props {
  translationKey: string
  metricUnit: UnitConfig
  imperialUnit: UnitConfig
  /** @deprecated No longer used in Yazio-style design */
  backgroundImage?: string
}

function getDefaultUnit(metricUnit: UnitConfig, imperialUnit: UnitConfig) {
  return navigator.language.toUpperCase().includes("US")
    ? imperialUnit.value
    : metricUnit.value
}

export default function UnitInputPage({
  translationKey,
  metricUnit,
  imperialUnit,
}: Props) {
  const { t } = useTranslation()

  const [unit, setUnit] = useState(() =>
    getDefaultUnit(metricUnit, imperialUnit),
  )
  const [value, setValue] = useState("")
  const [mascotState, setMascotState] = useState<MascotState>("idle")

  useEffect(() => {
    if (value.length > 0) {
      setMascotState("writing")
      const timer = setTimeout(() => setMascotState("idle"), 600)
      return () => clearTimeout(timer)
    }
  }, [value])

  const currentUnitConfig =
    unit === metricUnit.value ? metricUnit : imperialUnit

  const getMetricValue = () => {
    const parsedValue = parseFloat(value) || 0
    return unit === metricUnit.value
      ? parsedValue
      : parsedValue * imperialUnit.conversionFactor
  }

  const heading = t(`pages.onboarding.${translationKey}.heading`)
  const subtitle = t(`pages.onboarding.${translationKey}.info`)

  return (
    <Layout>
      <div className="mt-4 flex items-center gap-3">
        <Mascot state={mascotState} />
        <div className="speech-bubble flex-1">
          <h1 className="text-lg font-bold">{heading}</h1>
        </div>
      </div>

      {subtitle && <p className="custom-subheading mt-3">{subtitle}</p>}

      <Form method="post" className="custom-form">
        <div className="mt-8 flex grow flex-col items-center justify-center space-y-5">
          <NumberInputWithUnit
            value={value}
            onChange={setValue}
            unit={unit}
            onUnitChange={setUnit}
            units={[
              { value: metricUnit.value, label: metricUnit.label },
              { value: imperialUnit.value, label: imperialUnit.label },
            ]}
            placeholder={currentUnitConfig.placeholder}
          />
        </div>
        <div className="sticky bottom-0 -mx-5 bg-gradient-to-t from-white from-70% to-transparent px-5 pt-8 pb-4">
          <input name={translationKey} type="hidden" value={getMetricValue()} />
          <Button
            title={t("shared.continue")}
            type="submit"
            disabled={value.trim() === ""}
          />
        </div>
      </Form>
    </Layout>
  )
}
